import { useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { useCategories } from "@/context/CategoriesContext";
import {
  createCategory, updateCategory, deleteCategory, reorderCategories, getErrorMessage,
} from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from "lucide-react";

const emptyForm = { key: "", name_en: "", name_ar: "", is_active: true, kitchen_required: false };

export default function CategoryManager({ items }) {
  const { lang, t } = useLang();
  const { categories, reload } = useCategories();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const openNew = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      key: c.key,
      name_en: c.name_en,
      name_ar: c.name_ar,
      is_active: c.is_active,
      kitchen_required: !!c.kitchen_required,
    });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await updateCategory(editing.id, form);
      } else {
        const payload = { name_en: form.name_en, name_ar: form.name_ar, kitchen_required: !!form.kitchen_required };
        if (form.key) payload.key = form.key;
        await createCategory(payload);
      }
      toast.success(t.categorySaved);
      setDialogOpen(false);
      await reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const countInCat = (key) => items.filter((i) => i.category === key).length;

  const handleDelete = async (c) => {
    if (countInCat(c.key) > 0) {
      toast.error(t.categoryHasItems);
      return;
    }
    if (!window.confirm(t.confirmDelete)) return;
    try {
      await deleteCategory(c.id);
      toast.success(t.categoryDeleted);
      await reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const move = async (idx, delta) => {
    const newList = [...categories];
    const target = idx + delta;
    if (target < 0 || target >= newList.length) return;
    [newList[idx], newList[target]] = [newList[target], newList[idx]];
    try {
      await reorderCategories(newList.map((c) => c.id));
      await reload();
    } catch (err) {
      toast.error("Reorder failed");
    }
  };

  return (
    <div data-testid="categories-panel">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <p className="text-sm text-[#5C6B6D]">{t.reorder}</p>
        <Button onClick={openNew} data-testid="add-category-btn"
          className="rounded-full bg-[#0F5F63] hover:bg-[#0A4548] text-[#FDFBF7] uppercase tracking-widest text-xs h-11 px-5">
          <Plus className="w-4 h-4 me-2" /> {t.addCategory}
        </Button>
      </div>

      <div className="rounded-2xl bg-white border border-black/5 overflow-hidden">
        <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-[#F4F2EB] text-xs uppercase tracking-widest text-[#5C6B6D]">
          <div className="col-span-1">#</div>
          <div className="col-span-3">{t.nameEn}</div>
          <div className="col-span-3">{t.nameAr}</div>
          <div className="col-span-2">{t.categoryKey}</div>
          <div className="col-span-1">{t.tabItems}</div>
          <div className="col-span-2 text-end">{t.actions}</div>
        </div>
        {categories.length === 0 && (
          <div className="p-6 text-sm text-[#5C6B6D]">…</div>
        )}
        {categories.map((c, idx) => (
          <div key={c.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-t border-black/5 items-center text-sm" data-testid={`category-row-${c.id}`}>
            <div className="col-span-1 flex items-center gap-1">
              <span className="text-[#5C6B6D] w-5">{idx + 1}</span>
              <div className="flex flex-col">
                <button onClick={() => move(idx, -1)} disabled={idx === 0}
                  data-testid={`cat-up-${c.id}`}
                  className="w-5 h-5 text-[#5C6B6D] hover:text-[#0F5F63] disabled:opacity-30">
                  <ArrowUp className="w-3 h-3" />
                </button>
                <button onClick={() => move(idx, 1)} disabled={idx === categories.length - 1}
                  data-testid={`cat-down-${c.id}`}
                  className="w-5 h-5 text-[#5C6B6D] hover:text-[#0F5F63] disabled:opacity-30">
                  <ArrowDown className="w-3 h-3" />
                </button>
              </div>
            </div>
            <div className="col-span-3 font-medium">{c.name_en}</div>
            <div className="col-span-3 font-serif-ar">{c.name_ar}</div>
            <div className="col-span-2 font-mono text-xs text-[#5C6B6D]">
              <div>{c.key}</div>
              {c.kitchen_required && (
                <span className="mt-1 inline-block rounded-full bg-[#E27B58]/15 text-[#E27B58] text-[9px] uppercase tracking-widest px-2 py-0.5" data-testid={`cat-kitchen-tag-${c.id}`}>
                  {t.tagKitchen}
                </span>
              )}
            </div>
            <div className="col-span-1 text-[#5C6B6D]">{countInCat(c.key)}</div>
            <div className="col-span-2 flex justify-end gap-2">
              <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-cat-${c.id}`}
                className="rounded-full text-[#0F5F63] hover:bg-[#F4F2EB]"><Pencil className="w-4 h-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => handleDelete(c)} data-testid={`delete-cat-${c.id}`}
                className="rounded-full text-[#E27B58] hover:bg-[#E27B58]/10"><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-[#FDFBF7] rounded-2xl" data-testid="category-form-dialog">
          <DialogHeader>
            <DialogTitle className={`${lang === "ar" ? "font-serif-ar" : "font-serif-en"} text-2xl text-[#0F5F63]`}>
              {editing ? t.editCategory : t.addCategory}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div>
              <Label htmlFor="cat_en">{t.nameEn}</Label>
              <Input id="cat_en" required value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} data-testid="cat-name-en" />
            </div>
            <div>
              <Label htmlFor="cat_ar">{t.nameAr}</Label>
              <Input id="cat_ar" required value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} data-testid="cat-name-ar" />
            </div>
            <div>
              <Label htmlFor="cat_key">{t.categoryKey}</Label>
              <Input id="cat_key" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })}
                data-testid="cat-key" placeholder="e.g. seasonal_specials" />
              <p className="text-[10px] text-[#5C6B6D] mt-1">{t.categoryKeyHint}</p>
            </div>
            {editing && (
              <div className="flex items-center gap-3">
                <Switch id="cat_active" checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="cat-active-switch" />
                <Label htmlFor="cat_active" className="cursor-pointer">{form.is_active ? t.active : t.inactive}</Label>
              </div>
            )}
            <div className="flex items-start gap-3 rounded-xl bg-[#F4F2EB] px-4 py-3">
              <Switch
                id="cat_kitchen"
                checked={!!form.kitchen_required}
                onCheckedChange={(v) => setForm({ ...form, kitchen_required: v })}
                data-testid="cat-kitchen-switch"
              />
              <div>
                <Label htmlFor="cat_kitchen" className="cursor-pointer">{t.kitchenRequired}</Label>
                <p className="text-[10px] text-[#5C6B6D] mt-1">{t.kitchenRequiredHint}</p>
              </div>
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t.cancel}</Button>
              <Button type="submit" disabled={busy}
                className="bg-[#0F5F63] hover:bg-[#0A4548] text-[#FDFBF7]" data-testid="cat-save-btn">
                {t.save}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
